import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../common/LoadingSpinner";
import revisorService from "../../services/revisorService";
import { fmtDate, normalizeRole, PROFESSION_LABELS } from "./revisorUtils";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days between a confirmation and now; null when there is no date. */
function daysWaiting(confirmedAt) {
  if (!confirmedAt) return null;
  const date = new Date(confirmedAt);
  if (Number.isNaN(date.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / DAY_MS));
}

function waitingLabel(days) {
  if (days == null) return "Waiting time unknown";
  if (days === 0) return "Arrived today";
  if (days === 1) return "Waiting 1 day";
  return `Waiting ${days} days`;
}

/**
 * Urgency of a queue entry. Two weeks without a check is the point where an
 * entry stops being "recent" and starts holding up the apprentice.
 */
function urgencyClasses(days) {
  if (days == null) return "bg-gray-100 text-gray-600 border-gray-200";
  if (days >= 14) return "bg-red-50 text-red-700 border-red-200";
  if (days >= 7) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-ice-white text-slate-cloud border-pale-dawn";
}

function RevisorDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isRevisor = useMemo(
    () => user?.roles?.some((role) => normalizeRole(role) === "REVISOR"),
    [user],
  );

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await revisorService.getDashboard();
        setDashboard(data);
      } catch (err) {
        console.error(err);
        setError("Could not load revisor dashboard.");
      } finally {
        setLoading(false);
      }
    };

    if (isRevisor) run();
    else setLoading(false);
  }, [isRevisor]);

  const kpis = dashboard?.kpis || {};
  const pendingCases = useMemo(
    () => dashboard?.pendingCases || [],
    [dashboard],
  );

  // The backend returns the queue longest-waiting first, so the first entry is
  // the oldest and the counts below describe the real head of the queue.
  const openCount = kpis.openReviewCases || 0;
  const oldestDays = daysWaiting(pendingCases[0]?.confirmedAt);
  const overdueCount = pendingCases.filter(
    (item) => (daysWaiting(item.confirmedAt) ?? 0) >= 14,
  ).length;
  const missingReportInQueue = pendingCases.filter(
    (item) => !item.reportUploaded,
  ).length;

  const openProfessions = useMemo(
    () =>
      (dashboard?.professionOverview || [])
        .slice()
        .sort((a, b) => (b.openReviewCases || 0) - (a.openReviewCases || 0)),
    [dashboard],
  );

  const goToCase = (item) =>
    navigate(
      `/revisor/reviews?profession=${encodeURIComponent(item.professionType)}&learnerId=${item.learnerId}`,
    );

  if (!isRevisor) {
    return (
      <div className="min-h-screen bg-ice-white flex items-center justify-center p-6">
        <div className="card-brand p-8 max-w-xl text-center">
          <h2 className="text-xl font-bold text-grey-dusk mb-2">
            Access denied
          </h2>
          <p className="text-slate-cloud">
            This area is only available for users with the Revisor role.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner message="Loading revisor dashboard..." />;
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card-brand-elevated p-6 mb-5 bg-gradient-to-r from-ice-white to-pale-dawn/40">
          <h1 className="text-3xl font-bold text-grey-dusk mb-2">
            Revision Dashboard
          </h1>
          <p className="text-slate-cloud">
            Everything waiting for your check, most urgent first.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6">
            {error}
          </div>
        )}

        {/* The one number that decides whether there is work to do. */}
        <div className="card-brand p-6 mb-5 bg-white">
          <div className="flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="flex items-center gap-5 flex-1 min-w-0">
              <div
                className={`w-20 h-20 shrink-0 rounded-2xl flex items-center justify-center ${
                  openCount > 0
                    ? "bg-sunrise-red/10 text-sunrise-red"
                    : "bg-green-50 text-green-600"
                }`}
              >
                <span className="text-4xl font-bold">{openCount}</span>
              </div>

              <div className="min-w-0">
                <h2 className="text-xl font-bold text-grey-dusk">
                  {openCount === 0
                    ? "Nothing waiting for a check"
                    : `${openCount} case${openCount === 1 ? "" : "s"} waiting for your check`}
                </h2>
                <p className="text-sm text-slate-cloud mt-1">
                  {openCount === 0
                    ? "Every confirmed semester has been checked."
                    : oldestDays != null
                      ? `Longest waiting: ${oldestDays} day${oldestDays === 1 ? "" : "s"}`
                      : "Ordered by how long each case has been waiting."}
                </p>

                {openCount > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {overdueCount > 0 && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold border bg-red-50 text-red-700 border-red-200">
                        <i className="fas fa-triangle-exclamation mr-1.5"></i>
                        {overdueCount} waiting over 14 days
                      </span>
                    )}
                    {missingReportInQueue > 0 && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold border bg-amber-50 text-amber-700 border-amber-200">
                        <i className="fas fa-file-circle-exclamation mr-1.5"></i>
                        {missingReportInQueue} without a report
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => navigate("/revisor/reviews")}
              className="shrink-0 px-5 py-3 rounded-lg bg-sunrise-red text-white text-sm font-semibold hover:bg-sunrise-600 inline-flex items-center justify-center gap-2"
            >
              <i className="fas fa-list-check"></i>
              {openCount > 0 ? "Start reviewing" : "Open review page"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-5">
          {/* The queue itself is the main content of this page. */}
          <div className="card-brand p-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h2 className="text-lg font-bold text-grey-dusk">
                  Review queue
                </h2>
                <p className="text-xs text-slate-cloud mt-0.5">
                  Longest waiting first
                </p>
              </div>
              {pendingCases.length > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-ice-white border border-pale-dawn text-xs font-semibold text-grey-dusk">
                  {pendingCases.length}
                  {openCount > pendingCases.length ? ` of ${openCount}` : ""}
                </span>
              )}
            </div>

            {pendingCases.length === 0 ? (
              <div className="text-center py-10 rounded-xl border border-dashed border-pale-dawn bg-ice-white/50">
                <i className="fas fa-circle-check text-3xl text-green-500 mb-3 block"></i>
                <p className="text-sm font-semibold text-grey-dusk">
                  Queue is empty
                </p>
                <p className="text-xs text-slate-cloud mt-1">
                  Nothing is waiting for a check right now.
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {pendingCases.map((item) => {
                  const days = daysWaiting(item.confirmedAt);
                  return (
                    <li
                      key={`${item.learnerId}-${item.classId ?? "no-class"}-${item.semesterId}`}
                    >
                      <button
                        onClick={() => goToCase(item)}
                        className="w-full text-left rounded-xl border border-pale-dawn bg-white hover:border-sunrise-red/40 hover:bg-ice-white/60 transition-colors p-3 group"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-grey-dusk truncate group-hover:text-sunrise-red transition-colors">
                              {item.learnerName}
                            </div>
                            <div className="text-xs text-slate-cloud mt-0.5 flex flex-wrap items-center gap-x-2">
                              <span>
                                {PROFESSION_LABELS[item.professionType] ||
                                  item.professionType}
                              </span>
                              {item.semesterKey && (
                                <>
                                  <span className="text-gray-300">·</span>
                                  <span className="px-1.5 py-0.5 rounded bg-pale-dawn/60 text-grey-dusk font-medium">
                                    {item.semesterKey}
                                  </span>
                                </>
                              )}
                              {!item.reportUploaded && (
                                <>
                                  <span className="text-gray-300">·</span>
                                  <span className="text-amber-700 font-medium">
                                    No report
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          <span
                            className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${urgencyClasses(days)}`}
                          >
                            {waitingLabel(days)}
                          </span>
                        </div>

                        <div className="text-[11px] text-gray-400 mt-1.5">
                          Confirmed by apprentice {fmtDate(item.confirmedAt)}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="space-y-5">
            {/* Professions carrying open work, biggest backlog first. */}
            <div className="card-brand p-5">
              <h2 className="text-lg font-bold text-grey-dusk mb-3">
                Where the work sits
              </h2>
              {openProfessions.length === 0 ? (
                <p className="text-sm text-slate-cloud">
                  No professions with reviewable data.
                </p>
              ) : (
                <ul className="space-y-2">
                  {openProfessions.map((item) => (
                    <li key={item.professionType}>
                      <button
                        onClick={() =>
                          navigate(
                            `/revisor/reviews?profession=${encodeURIComponent(item.professionType)}`,
                          )
                        }
                        className="w-full text-left rounded-xl border border-pale-dawn bg-white hover:border-sunrise-red/40 transition-colors px-3 py-2.5 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-grey-dusk truncate">
                            {PROFESSION_LABELS[item.professionType] ||
                              item.professionType}
                          </div>
                          <div className="text-[11px] text-slate-cloud">
                            {item.learners} apprentice
                            {item.learners === 1 ? "" : "s"} ·{" "}
                            {item.checkedCases} checked
                          </div>
                        </div>
                        <span
                          className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-bold border ${
                            item.openReviewCases > 0
                              ? "bg-sunrise-red/10 text-sunrise-red border-sunrise-red/20"
                              : "bg-green-50 text-green-700 border-green-200"
                          }`}
                        >
                          {item.openReviewCases > 0
                            ? `${item.openReviewCases} open`
                            : "Clear"}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Context, not action: kept small on purpose. */}
            <div className="card-brand p-5">
              <h2 className="text-sm font-bold text-grey-dusk mb-3">
                Background
              </h2>
              <dl className="space-y-2 text-sm">
                <StatRow
                  label="Waiting on apprentice confirmation"
                  value={kpis.missingConfirmations}
                  hint="Not actionable for you yet"
                />
                <StatRow
                  label="Semesters without a report"
                  value={kpis.missingReports}
                />
                <StatRow label="Already checked" value={kpis.checkedCases} />
                <StatRow
                  label="Apprentices with reviewable data"
                  value={kpis.totalLearnersWithReviewableData}
                />
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatRow({ label, value, hint }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-pale-dawn/70 last:border-0 pb-2 last:pb-0">
      <div className="min-w-0">
        <dt className="text-xs text-grey-dusk">{label}</dt>
        {hint && <p className="text-[10px] text-gray-400">{hint}</p>}
      </div>
      <dd className="shrink-0 text-sm font-bold text-grey-dusk">
        {value || 0}
      </dd>
    </div>
  );
}

export default RevisorDashboardPage;
